import { useState } from 'react';
import { problemDetail } from '@/shared/api/problem';
import {
  getGetApiV1VolunteersQueryKey,
  useGetApiV1Volunteers,
  usePostApiV1VolunteersUserIdReview,
} from '@/shared/api/generated/volunteers/volunteers';
import type {
  GetApiV1VolunteersParams,
  VolunteerSummaryDto,
} from '@/shared/api/generated/model';
import { ApprovalStatus } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { formatDateTime } from '@/shared/lib/format/dates';
import { Button } from '@/shared/ui/Button';
import { QueryError } from '@/shared/ui/QueryError';
import { Chip, toneForApproval } from '@/shared/ui/Chip';
import { Pagination } from '@/shared/ui/Pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { DocumentLink } from '@/features/volunteers/ui/DocumentLink';
import { RejectVolunteerDialog } from '@/features/volunteers/ui/RejectVolunteerDialog';
import { useQueryClient } from '@tanstack/react-query';
import { LiveEvery, liveEvery } from '@/shared/lib/live';

/**
 * The document-review queue §23.8 requires: approval is the act that grants a volunteer
 * the field-volunteer role, and therefore access to mission data — including the
 * positions of people at risk.
 *
 * Server state comes from the generated hooks only (§5.2, §5.3). Nothing is mirrored into
 * `useState`; the list after a review is whatever the refetch returns.
 */
/**
 * ⚠️ **The list is paged, and until 5 September 2026 this panel did not know it.** The API answers
 * twenty rows a page, oldest registration first — right for a queue, where the first to wait is the
 * first to be seen. Without a pager the twenty seeded accounts filled «الكل» and «معتمد» for good,
 * and every volunteer who registered after them — including the one approved a minute earlier —
 * was nowhere on the panel. Measured: the administrator looked for the volunteer they had just
 * approved and could not find them.
 */
const PAGE_SIZE = 20;

export function VolunteerQueue() {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ApprovalStatus | 'all'>(ApprovalStatus.Pending);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // The volunteer being refused, or null when the dialog is closed. Held as the whole row rather than
  // an id so the dialog can name the person — an overlay hides the table behind it.
  const [rejecting, setRejecting] = useState<VolunteerSummaryDto | null>(null);

  // Typed as the generated params, so a filter this panel invents that the API does not
  // accept is a compile error rather than a silently ignored query string.
  const params: GetApiV1VolunteersParams =
    status === 'all'
      ? { page, pageSize: PAGE_SIZE }
      : { approvalStatus: status, page, pageSize: PAGE_SIZE };

  const volunteers = useGetApiV1Volunteers(params, liveEvery(LiveEvery.queue));

  const review = usePostApiV1VolunteersUserIdReview({
    mutation: {
      onSuccess: async () => {
        setError(null);
        // Invalidate rather than patching a local array: the row may now belong to a
        // different filter, and the server decides that, not this component.
        await queryClient.invalidateQueries({ queryKey: getGetApiV1VolunteersQueryKey(params) });
      },
      onError: (cause: unknown) => {
        // The API's own sentence first (§13's `detail`). It says «لا يمكن اعتماد ملفك بنفسك» where a
        // generic permission message would send the operator hunting for a role change.
        setError(problemDetail(cause) ?? t.volunteers.reviewFailed);
      },
    },
  });

  const approve = (volunteer: VolunteerSummaryDto) => {
    review.mutate({ userId: volunteer.userId, data: { approve: true, reason: null } });
  };

  // §23.8 and the command validator both require a reason for a refusal: a decision nobody can explain
  // to the volunteer who was refused is not defensible. The reason is collected in a real dialog rather
  // than a native prompt — see RejectVolunteerDialog.
  const reject = (volunteer: VolunteerSummaryDto, reason: string) => {
    setRejecting(null);
    review.mutate({ userId: volunteer.userId, data: { approve: false, reason } });
  };

  const filters: { value: ApprovalStatus | 'all'; label: string }[] = [
    { value: ApprovalStatus.Pending, label: t.approvalStatus.Pending },
    { value: ApprovalStatus.Approved, label: t.approvalStatus.Approved },
    { value: ApprovalStatus.Rejected, label: t.approvalStatus.Rejected },
    { value: 'all', label: t.volunteers.filterAll },
  ];

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1.5">
        <h1 className="text-2xl font-bold tracking-tight">{t.volunteers.queueHeading}</h1>
        <p className="text-sm text-muted-foreground">{t.volunteers.queueLede}</p>
      </header>

      <div className="flex flex-wrap gap-2">
        {filters.map((filter) => (
          <button
            key={filter.value}
            type="button"
            aria-pressed={status === filter.value}
            onClick={() => {
              setStatus(filter.value);
              // A new filter starts at its first page: page three of «معتمد» means nothing on «مرفوض».
              setPage(1);
            }}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
              status === filter.value
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground hover:border-primary'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {error ? (
        <p role="alert" className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {volunteers.isPending ? (
        <p className="text-sm text-muted-foreground">{t.app.loading}</p>
      ) : volunteers.isError ? (
        <QueryError error={volunteers.error} onRetry={() => void volunteers.refetch()} />
      ) : volunteers.data.items.length === 0 ? (
        <p className="rounded-md border border-border bg-surface px-4 py-6 text-center text-sm text-muted-foreground">
          {t.volunteers.empty}
        </p>
      ) : (
        <div className="rounded-lg border border-border bg-surface">
          <Table className="min-w-3xl">
            <TableHeader>
              <TableRow className="bg-surface-elevated text-xs uppercase tracking-wider text-muted-foreground">
                <TableHead scope="col">{t.volunteers.name}</TableHead>
                <TableHead scope="col">{t.volunteers.region}</TableHead>
                <TableHead scope="col">{t.volunteers.type}</TableHead>
                <TableHead scope="col">{t.volunteers.equipment}</TableHead>
                <TableHead scope="col">{t.volunteers.documents}</TableHead>
                <TableHead scope="col">{t.volunteers.status}</TableHead>
                <TableHead scope="col">{t.volunteers.registered}</TableHead>
                <TableHead scope="col">{t.volunteers.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {volunteers.data.items.map((volunteer) => (
                <TableRow key={volunteer.userId}>
                  <TableCell className="font-medium">{volunteer.fullName}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {volunteer.region} — {volunteer.city}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {volunteerTypeLabel(volunteer.volunteerType, t.volunteerType)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{equipment(volunteer, t)}</TableCell>
                  <TableCell>
                    {/* The row says whether a document EXISTS, never its key. The file itself is
                        reached through a presigned URL fetched on click, so that each audited read
                        (§12.7) corresponds to a reviewer actually looking at one person's papers —
                        not to a list being rendered. */}
                    {volunteer.hasIdDocument ? (
                      <DocumentLink userId={volunteer.userId} kind="id" />
                    ) : (
                      <span className="text-muted-foreground">{t.volunteers.documentMissing}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip tone={toneForApproval(volunteer.approvalStatus)}>
                      {approvalLabel(volunteer.approvalStatus, t.approvalStatus)}
                    </Chip>

                    {/* ⚠️ The reason survives a resubmission, so a PENDING row can carry one — and
                        that is exactly when the reviewer needs it. A volunteer who was refused and
                        has sent new documents returns to this queue, and without the previous
                        reason the reviewer opens them as a fresh application with no idea what was
                        wrong last time or what to look at now.

                        Approval clears it, so an approved row never shows an explanation of a
                        refusal that was overturned. */}
                    {/* ⚠️ **A suspended account looked exactly like an active one.** The row has
                        always carried `userStatus` and nothing drew it — so a reviewer reading this
                        queue could approve documents for somebody the platform will not let sign in
                        at all (§12.1 refuses a suspended account a code, a verification and a
                        refresh). Drawn only when it is NOT active: a column that says «نشط» on
                        every row of every page is a column people stop reading. */}

                    {/* ⚠️⚠️ **And `!== 'Active'` called every NEW registration suspended.** A volunteer
                        who has registered and not yet verified a code is `Pending`, which is the
                        ordinary first state of every account — `VerifyOtpCommandHandler` activates it
                        on the first verification and refuses only `Suspended`. Until 21 September 2026
                        this queue told the reviewer «الحساب موقوف — لا يستطيع الدخول» about people
                        nobody had suspended, on the screen where accounts are judged. Two states,
                        two sentences, and only one of them is a problem. */}
                    {volunteer.userStatus === 'Suspended' ? (
                      <p
                        className="mt-1 text-xs text-danger"
                        data-testid={`user-status-${volunteer.userId}`}
                      >
                        {t.volunteers.accountSuspended}
                      </p>
                    ) : volunteer.userStatus === 'Pending' ? (
                      <p
                        className="mt-1 text-xs text-muted-foreground"
                        data-testid={`user-status-${volunteer.userId}`}
                      >
                        {t.volunteers.accountNeverSignedIn}
                      </p>
                    ) : null}

                    {volunteer.rejectionReason ? (
                      <p
                        className="mt-1 max-w-64 text-xs text-muted-foreground"
                        data-testid={`rejection-reason-${volunteer.userId}`}
                      >
                        <span className="text-danger">{t.volunteers.previousRejection}</span>{' '}
                        {volunteer.rejectionReason}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell className="tabular text-muted-foreground">
                    {formatDateTime(volunteer.registeredAt, locale)}
                  </TableCell>
                  <TableCell>
                    {volunteer.approvalStatus === ApprovalStatus.Pending ? (
                      <div className="flex gap-2">
                        {/* ⚠️ Approving a volunteer with no identity document on file is approving
                            nothing — §23.8 makes this a decision ABOUT documents. The server
                            refuses it with a 409; disabling the button here is so the reviewer
                            sees why before they click, rather than after.

                            Refusing stays enabled on purpose: somebody who uploaded nothing is
                            exactly who you need to refuse, and blocking that would leave them in
                            this queue for ever. */}
                        <Button
                          onClick={() => approve(volunteer)}
                          disabled={review.isPending || !volunteer.hasIdDocument}
                          title={volunteer.hasIdDocument ? undefined : t.volunteers.approveNeedsDocument}
                        >
                          {t.volunteers.approve}
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => setRejecting(volunteer)}
                          disabled={review.isPending}
                        >
                          {t.volunteers.reject}
                        </Button>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        
          <Pagination
            page={volunteers.data.page ?? page}
            pageSize={volunteers.data.pageSize ?? PAGE_SIZE}
            totalCount={volunteers.data.totalCount ?? volunteers.data.items.length}
            onPageChange={setPage}
          />
</div>
      )}
      {rejecting ? (
        <RejectVolunteerDialog
          volunteerName={rejecting.fullName}
          open
          onOpenChange={(next) => {
            if (!next) {
              setRejecting(null);
            }
          }}
          onConfirm={(reason) => reject(rejecting, reason)}
        />
      ) : null}
    </section>
  );
}

/**
 * The API sends enum NAMES as strings (the backend's JsonStringEnumConverter), so the
 * lookup is by key. An unknown key falls back to the raw value rather than to an empty
 * cell: if the server adds a volunteer type before this panel knows about it, showing
 * `NewType` is honest, and showing nothing hides a row's meaning.
 */
function volunteerTypeLabel(value: string, labels: Record<string, string>): string {
  return labels[value] ?? value;
}

function approvalLabel(value: string, labels: Record<string, string>): string {
  return labels[value] ?? value;
}

function equipment(
  volunteer: VolunteerSummaryDto,
  t: { volunteers: { has4wd: string; hasRadio: string; hasGps: string; noEquipment: string } },
): string {
  const declared = [
    volunteer.has4wd ? t.volunteers.has4wd : null,
    volunteer.hasRadio ? t.volunteers.hasRadio : null,
    volunteer.hasGpsDevice ? t.volunteers.hasGps : null,
  ].filter((item): item is string => item !== null);

  return declared.length > 0 ? declared.join(' · ') : t.volunteers.noEquipment;
}
